// src/components/AccessibilityStatementModal.tsx
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Divider,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

type Props = {
  open: boolean;
  onClose: () => void;
};

const svgGrey = "#BFBFBF";

export default function AccessibilityStatementModal({ open, onClose }: Props) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="accessibility-statement-title"
      scroll="paper"
      dir="rtl" // ✅ enforce RTL on dialog root
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 2,
          direction: "rtl", // ✅ ensure the Paper is RTL
        },
      }}
    >
      <DialogTitle
        id="accessibility-statement-title"
        dir="rtl" // ✅ enforce on title
        style={{ textAlign: "right" }}
        sx={{
          pr: 2,
          pl: 6, // space for close button on the right (RTL)
          fontWeight: 800,
          position: "relative",
          textAlign: "right",
        }}
      >
        הצהרת נגישות
        <IconButton
          onClick={onClose}
          aria-label="סגור"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        style={{ textAlign: "right" }}
        dir="rtl" // ✅ enforce on the content DOM node
        dividers
        sx={{
          direction: "rtl", // ✅ browser layout direction
          textAlign: "right", // ✅ align all text to right
          pt: 2,
          // Paragraphs
          "& p": { mb: 1.25, lineHeight: 1.8 },
          // Lists (logical properties for RTL)
          "& ul, & ol": {
            listStylePosition: "inside",
            paddingInlineStart: 0, // remove LTR default
            paddingInlineEnd: "1.25rem",
            marginInlineStart: 0,
            marginInlineEnd: 0,
            mb: 1.5,
            textAlign: "right",
          },
          "& li": { mb: 0.75 },
        }}
      >
        {/* URL block (keep LTR so punctuation/URL show correctly) */}
        <Box sx={{ mb: 1 }} style={{ textAlign: "right" }}>
          <Typography dir="rtl" variant="subtitle2" sx={{ fontWeight: 700 }}>
            אתר האינטרנט המרכזי של חברת סופרגז פאוור:
          </Typography>
          <Box dir="ltr">
            <Link
              href="https://www.supergas-power.co.il"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              https://www.supergas-power.co.il
            </Link>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Typography dir="rtl" style={{ textAlign: "right" }}>
          חברת סופרגז פאוור מייחסת חשיבות רבה להנגשת אתר האינטרנט וכלל השירותים
          הניתנים על מנת למנוע אפליה ולתת שירות נגיש גם עבור אנשים עם מוגבלויות
          אשר מהווים כ־20% מהאוכלוסייה כולה.
        </Typography>

        <Typography dir="rtl" style={{ textAlign: "right" }}>
          אתר האינטרנט של החברה עבר סקר נגישות נרחב על מנת למצוא את ליקויי
          הנגישות ולתקנם, ובכך להתאימם לת״י 5568 חלק 1 ומסמך WCAG 2.0 הבינלאומי
          ברמה AA.
        </Typography>

        <Typography
          dir="rtl"
          variant="subtitle2"
          sx={{ fontWeight: 700, mt: 2 }}
        >
          התאמות שבוצעו באתר (לדוגמה):
        </Typography>
        <ul dir="rtl" style={{ textAlign: "right" }}>
          <li>הוספת אפשרות לניווט באמצעות המקלדת</li>
          <li>הוספת טקסט חלופי לתמונות</li>
          <li>חידוד ניגודיות הצבעים</li>
          <li>שימוש נכון בכותרות</li>
          <li>תוויות לשדות טופס ועוד</li>
        </ul>

        <Typography dir="rtl">
          לשם קבלת חוויית גלישה מיטבית עם תוכנת הקראת מסך, אנו ממליצים על שימוש
          בתוכנת NVDA העדכנית ביותר ובדפדפן Chrome.
        </Typography>

        <Typography dir="rtl" sx={{ mt: 2 }}>
          על אף ההתאמות שנעשו באתר האינטרנט, באם נתקלתם בבעיית נגישות נשמח
          לדיווח על כך לרכזת הנגישות של החברה, על מנת שנתקן בעיה זו באופן מיידי:
        </Typography>

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
            my: 1.5,
            background: "rgba(0,0,0,0.02)",
          }}
        >
          <Typography>רכזת נגישות: גברת עינת מאור</Typography>
          <Typography>טלפון: 09-8308759</Typography>
          <Typography dir="rtl">מייל: maore@supergas-power.co.il</Typography>
        </Box>

        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          הצהרת הנגישות עודכנה בתאריך 29.9.2024 ורלוונטית לתכנים שהיו קיימים
          באתר בתאריך זה.
        </Typography>
      </DialogContent>

      {/* Subtle footer tint using svgGrey */}
      <Box
        sx={{
          height: 6,
          width: "100%",
          background: svgGrey,
          opacity: 0.25,
        }}
      />
    </Dialog>
  );
}
